import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { BedDouble, Settings, ChevronDown, ChevronRight, Check } from 'lucide-react';
import { totalCapacity } from '../../constants/roomConfig';

// Capacity fallback shared by the hotel tally, room-type total, and each
// unit row: prefer the unit's own capacity; otherwise derive it from its
// bed configuration if configured; otherwise 0 (no data).
const unitCapacity = (u) => u.capacity ?? (u.config_name ? totalCapacity(u.beds) : 0);

/**
 * Collapsible hotel/room-type/unit availability picker for
 * ParticipantBookingPanel's create-mode flow.
 */
const HotelAvailabilityList = ({ availability, selectedUnit, collapsedHotels, onToggleHotel, onSelectUnit, onOpenConfig }) => {
  const [hoveredUnit, setHoveredUnit] = useState(null); // { unitId, rect, occupied, capacity, rows } | null
  const navigate = useNavigate();

  if (availability.length === 0) {
    return (
      <div className="pbt-no-availability">
        <div className="pbt-no-availability__icon">
          <BedDouble size={22} strokeWidth={1.8} />
        </div>
        <p className="pbt-no-availability__title">No available rooms for this date range</p>
        <p className="pbt-no-availability__sub">
          None of this competition&rsquo;s hotel rooms are configured to cover these dates.
        </p>
        <button type="button" className="pbt-no-availability__cta" onClick={() => navigate('/ems/hotels')}>
          Go to Rooms
        </button>
      </div>
    );
  }

  return (
    <div className="pbt-panel__hotels pbt-panel__hotels--scroll">
      {availability.map((hotel) => {
        const collapsed = collapsedHotels.has(hotel.hotel_id);
        const hotelUnitCount = hotel.room_types.reduce((s, rt) => s + rt.units.length, 0);
        return (
          <div key={hotel.hotel_id} className="pbt-hotel">
            <button
              type="button"
              className="pbt-hotel__toggle"
              onClick={() => onToggleHotel(hotel.hotel_id)}
            >
              {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
              <span className="pbt-hotel__name">{hotel.hotel_name}</span>
              <span className="pbt-hotel__tally">
                {hotelUnitCount} unit{hotelUnitCount !== 1 ? 's' : ''}
              </span>
            </button>
            {!collapsed && hotel.room_types.map((roomType) => {
              const unitCount = roomType.units.length;
              const freeTotal = roomType.units.reduce((s, u) => s + (u.beds_free || 0), 0);
              const capacityTotal = roomType.units.reduce((s, u) => s + unitCapacity(u), 0);
              return (
              <div key={roomType.room_id} className="pbt-hotel__room-type">
                <div className="pbt-room-type-block">
                  <div className="pbt-room-type-block__header">
                    <span className="pbt-room-type-block__name">{roomType.room_type}</span>
                    <Settings
                      size={13}
                      className="pbt-config-gear"
                      title={`Manage configurations for ${roomType.room_type}`}
                      onClick={() => onOpenConfig({ _id: roomType.room_id, room_name: roomType.room_type })}
                    />
                    <span className="pbt-hotel__room-type-summary-inline">{unitCount} room{unitCount === 1 ? '' : 's'}</span>
                    {capacityTotal > 0 && freeTotal === capacityTotal && (
                      <span className="pbt-hotel__room-type-avail">
                        <span className="pbt-hotel__room-type-avail-dot" />
                        All {capacityTotal} beds free
                      </span>
                    )}
                  </div>
                  <div className="pbt-unit-ledger">
                    <div className="pbt-unit-ledger__gh">Room</div>
                    <div className="pbt-unit-ledger__gh">Configuration</div>
                    <div className="pbt-unit-ledger__gh">PAX</div>
                    <div className="pbt-unit-ledger__gh pbt-unit-ledger__gh--right">Price / night</div>

                    {roomType.units.map((unit) => {
                    const active = selectedUnit?.room_unit_id === unit.room_unit_id;
                    const configured = Boolean(unit.config_name);
                    const capacity = unitCapacity(unit);
                    const metaParts = configured
                      ? [...(unit.beds || []).map((b) => `${b.count}× ${b.type}`), ...(unit.features || [])]
                      : [];
                    const isFull = unit.beds_free <= 0;
                    const occupants = unit.occupants || [];
                    const occupantRows = occupants.map((o) => (o.is_guest_only ? `${o.name} (Guest)` : o.name));
                    const occupantLabel = occupantRows.length > 0 ? occupantRows.join(', ') : null;

                    const rowProps = isFull
                      ? {}
                      : {
                          role: 'button',
                          tabIndex: 0,
                          'aria-pressed': active,
                          onClick: () => onSelectUnit(hotel, roomType, unit),
                          onKeyDown: (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onSelectUnit(hotel, roomType, unit);
                            }
                          },
                        };

                    // PAX pip meter — one bar per person, capped at 10.
                    // Gold = occupied, grey = still free (opposite of the old
                    // Beds-free meter's gold-means-free convention — this is
                    // PAX read as "how full is this room", not "how many
                    // beds are left"). Proportionally scaled above capacity
                    // 10, same as the old meter.
                    const occupiedCount = Math.max(capacity - unit.beds_free, 0);
                    const paxPipCount = Math.min(capacity, 10) || 0;
                    const paxOccupiedPips = capacity > 10
                      ? Math.round((occupiedCount / capacity) * 10)
                      : occupiedCount;

                    return (
                      <div
                        key={unit.room_unit_id}
                        style={{ display: 'contents' }}
                        className={`pbt-unit-row${active ? ' pbt-unit-row--selected' : ''}${isFull ? ' pbt-unit-row--full' : ''}`}
                        {...rowProps}
                        onMouseEnter={(e) => {
                          if (occupants.length > 0) {
                            const cap = unit.beds_free + occupants.length;
                            // e.currentTarget is this row wrapper, which is
                            // display:contents and therefore has no box of
                            // its own — getBoundingClientRect() on it always
                            // returns an all-zero rect. Measure its first and
                            // last cell instead (both real .pbt-unit-ledger__cell
                            // boxes) to reconstruct the row's visual bounds:
                            // top-aligned with the row, positioned past its
                            // right edge, same as the old full-width card.
                            const firstRect = e.currentTarget.firstElementChild.getBoundingClientRect();
                            const lastRect = e.currentTarget.lastElementChild.getBoundingClientRect();
                            setHoveredUnit({
                              unitId: unit.room_unit_id,
                              rect: { top: firstRect.top, right: lastRect.right },
                              occupied: occupants.length,
                              capacity: cap,
                              rows: occupantRows,
                            });
                          }
                        }}
                        onMouseLeave={() => setHoveredUnit((h) => (h?.unitId === unit.room_unit_id ? null : h))}
                      >
                        <div className="pbt-unit-ledger__cell">
                          <span className="pbt-unit-name">
                            {active && (
                              <span className="pbt-unit-check">
                                <Check size={9} strokeWidth={3.5} />
                              </span>
                            )}
                            Room {unit.room_label}
                          </span>
                          {isFull && <span className="pbt-unit-full-label">Full</span>}
                          {occupantLabel && (
                            <span className="pbt-unit-occupant-label">
                              {isFull ? 'Occupied by: ' : 'Also: '}{occupantLabel}
                            </span>
                          )}
                        </div>
                        <div className="pbt-unit-ledger__cell">
                          {configured ? (
                            <>
                              <span className="pbt-unit-config">{unit.config_name}</span>
                              {metaParts.length > 0 && <span className="pbt-unit-config-meta">{metaParts.join(' · ')}</span>}
                            </>
                          ) : (
                            <span className="pbt-unit-config--none">Not configured</span>
                          )}
                        </div>
                        <div className="pbt-unit-ledger__cell">
                          {capacity > 0 && (
                            <span className="pbt-unit-pax">
                              <span className="pbt-unit-pips" role="img" aria-label={`${unit.beds_free} of ${capacity} PAX free`}>
                                {Array.from({ length: paxPipCount }, (_, i) => (
                                  <i key={i} className={`pbt-unit-pip${i < paxOccupiedPips ? '' : ' pbt-unit-pip--empty'}`} />
                                ))}
                              </span>
                              <span className="pbt-unit-pax-n">
                                {unit.beds_free}<em>&nbsp;/&nbsp;{capacity}</em>
                              </span>
                            </span>
                          )}
                        </div>
                        <div className="pbt-unit-ledger__cell pbt-unit-ledger__cell--right">
                          <span className="pbt-unit-price">${unit.price}<small>/night</small></span>
                        </div>
                      </div>
                    );
                  })}
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        );
      })}
      {hoveredUnit && createPortal(
        <div
          className="pbt-unit-occ-popover"
          style={{ top: hoveredUnit.rect.top, left: hoveredUnit.rect.right + 8 }}
        >
          <div className="pbt-unit-occ-popover__label">
            {hoveredUnit.occupied} of {hoveredUnit.capacity} occupied
          </div>
          <div className="pbt-unit-occ-popover__bar">
            <div
              className="pbt-unit-occ-popover__bar-fill"
              style={{ width: `${(hoveredUnit.occupied / hoveredUnit.capacity) * 100}%` }}
            />
          </div>
          {hoveredUnit.rows.map((r, i) => (
            <div key={r + i} className="pbt-unit-occ-popover__row">{r}</div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

export default HotelAvailabilityList;
